import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, session } from "../shared/utils";
import { Icon, SafeZapBolt } from "../shared/components";

// ── Live Operation Card — cycles through fake real-time events ─────────────────
const LIVE_EVENTS = [
  { initials: "MV", text: <>Maya Vitória aguarda resposta de <strong>Damiana</strong></>, time: "há 12s" },
  { initials: "JN", text: <>Janyer marcado como <strong>Negócio fechado</strong></>, time: "há 1m" },
  { initials: "CJ", text: <>Cleo Jr · ticket <strong>crítico</strong> escalado para Suporte</>, time: "há 3m" },
  { initials: "RA", text: <>Rodrigo Alves · nova conversa na fila <strong>Comercial</strong></>, time: "há 5s" },
  { initials: "TS", text: <>Thais S. · <strong>template</strong> enviado com sucesso</>, time: "há 22s" },
  { initials: "BL", text: <>Bruno Lima · conversa <strong>finalizada</strong> pelo atendente</>, time: "há 45s" },
];

function LiveOperationCard() {
  const [activeCount, setActiveCount] = useState(42);
  const [events, setEvents] = useState(LIVE_EVENTS.slice(0, 3));
  const [nextIdx, setNextIdx] = useState(3);
  const [animKey, setAnimKey] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveCount((n) => n + Math.floor(Math.random() * 3) - 1);
      setEvents((prev) => {
        const next = LIVE_EVENTS[nextIdx % LIVE_EVENTS.length];
        setNextIdx((i) => i + 1);
        setAnimKey((k) => k + 1);
        return [next, prev[0], prev[1]];
      });
    }, 3800);
    return () => clearInterval(interval);
  }, [nextIdx]);

  return (
    <div className="side-live-card">
      <div className="side-live-header">
        <span className="side-live-label">
          <span className="side-live-dot" />
          OPERAÇÃO AO VIVO
        </span>
        <span className="side-live-count">{activeCount} conversas ativas</span>
      </div>
      <div className="side-live-feed">
        {events.map((ev, i) => (
          <div key={`${animKey}-${i}`} className={`side-live-item${i === 0 ? " side-live-item-new" : ""}`}>
            <span className="side-live-avatar">
              {ev.initials}
            </span>
            <span className="side-live-text">{ev.text}</span>
            <span className="side-live-time">{ev.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const SAVED_EMAIL_KEY = "safezap_remembered_email";

function LoginScreen({ setToken, setUser, onLogin }) {
  const _saved = localStorage.getItem(SAVED_EMAIL_KEY) || "";
  const [email, setEmail] = useState(_saved || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(!!_saved);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState("");

  // ── OAuth callback: lê ?oauth_token= ou ?oauth_error= da URL ──────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthToken = params.get("oauth_token");
    const oauthError = params.get("oauth_error");

    if (oauthToken) {
      try {
        const payload = JSON.parse(atob(oauthToken.split(".")[1]));
        const user = { id: payload.id, name: payload.name, email: payload.email, role: payload.role, company_id: payload.company_id };
        window.history.replaceState({}, "", window.location.pathname);
        if (onLogin) {
          onLogin(oauthToken, user);
        } else {
          session.save(oauthToken, user);
          setToken(oauthToken);
          setUser(user);
        }
      } catch {
        setError("Erro ao processar login social. Tente novamente.");
      }
    } else if (oauthError) {
      setError(decodeURIComponent(oauthError));
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [setToken, setUser]);

  async function login(e) {
    e.preventDefault();
    setLoading(true);
    setError("");

    // ── Tentativa com retry automático em caso de falha de rede ─────────────
    // Tenta 2 vezes com 2s de espera entre elas para cobrir instabilidades
    // transitórias do servidor (Railway cold start, breve queda de rede).
    const MAX_ATTEMPTS = 2;
    let lastErr = null;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        const response = await axios.post(
          `${API}/auth/login`,
          { email, password },
          {
            timeout: 15000, // 15s — evita travar indefinidamente se o servidor não responder
            headers: { "Content-Type": "application/json" },
          }
        );

        // Sucesso — salvar email se marcado
        if (remember) {
          localStorage.setItem(SAVED_EMAIL_KEY, email);
        } else {
          localStorage.removeItem(SAVED_EMAIL_KEY);
        }

        if (onLogin) {
          onLogin(response.data.token, response.data.user);
        } else {
          session.save(response.data.token, response.data.user);
          setToken(response.data.token);
          setUser(response.data.user);
        }
        setLoading(false);
        return; // Login bem-sucedido — encerrar
      } catch (err) {
        lastErr = err;

        // Erro de autenticação (4xx com mensagem do servidor) — não retentar
        if (err.response?.status >= 400 && err.response?.status < 500) break;

        // Erro de rede ou timeout (sem resposta) — retentar após pausa
        if (attempt < MAX_ATTEMPTS) {
          setRetrying(true);
          await new Promise((resolve) => setTimeout(resolve, 2000));
          setRetrying(false);
        }
      }
    }

    // Exibir mensagem de erro mais informativa baseada no tipo de falha
    const serverMsg = lastErr?.response?.data?.error;
    const status    = lastErr?.response?.status;

    if (serverMsg) {
      setError(serverMsg);
    } else if (lastErr?.code === "ECONNABORTED" || lastErr?.message?.includes("timeout")) {
      setError("O servidor demorou para responder. Verifique sua conexão e tente novamente.");
    } else if (!lastErr?.response) {
      setError("Não foi possível conectar ao servidor. Verifique sua conexão com a internet.");
    } else if (status === 503) {
      setError("Servidor temporariamente indisponível. Tente novamente em alguns segundos.");
    } else {
      setError("Erro ao fazer login. Tente novamente.");
    }

    setRetrying(false);
    setLoading(false);
  }

  return (
    <div className="login-v2">
      {/* LEFT — premium dark side */}
      <aside className="login-side">
        <div className="side-content">
          <div className="side-brand szIn szIn-1">
            <SafeZapBolt size={32} id="login" />
            <div>
              <div className="side-brand-name">Safe<span className="zap">Zap</span></div>
              <div className="side-brand-sub">CRM Inteligente</div>
            </div>
          </div>

          <div className="side-hero">
            <div className="side-eyebrow szIn szIn-2">
              <span className="side-eyebrow-dot" />
              PLATAFORMA WHATSAPP · CRM
            </div>
            <h1 className="side-headline szIn szIn-3">
              Atendimento rápido,<br />
              CRM organizado e<br />
              operação <em>pronta pra escalar</em>.
            </h1>
            <p className="side-lead szIn szIn-4">
              Conversas, responsáveis, lead pipeline e contexto do cliente em uma interface pensada
              pra responder rápido e vender melhor.
            </p>

            <div className="side-features">
              <div className="side-feat szIn szIn-5">
                <div className="side-feat-icon"><Icon name="chat" size={14} /></div>
                <div className="side-feat-title">Inbox em tempo real</div>
                <div className="side-feat-desc">Conversa, CRM e histórico no mesmo fluxo.</div>
              </div>
              <div className="side-feat szIn szIn-6">
                <div className="side-feat-icon"><Icon name="deal" size={14} /></div>
                <div className="side-feat-title">Lead pipeline</div>
                <div className="side-feat-desc">Leads em Kanban com responsável e etapa.</div>
              </div>
              <div className="side-feat szIn szIn-7">
                <div className="side-feat-icon"><Icon name="whatsapp" size={14} /></div>
                <div className="side-feat-title">WhatsApp conectado</div>
                <div className="side-feat-desc">QR, templates, janela 24h centralizados.</div>
              </div>
              <div className="side-feat szIn szIn-8">
                <div className="side-feat-icon"><Icon name="bell" size={14} /></div>
                <div className="side-feat-title">SLA & alertas</div>
                <div className="side-feat-desc">Sinaliza conversas críticas em tempo real.</div>
              </div>
            </div>
          </div>

          {/* OPERAÇÃO AO VIVO */}
          <div className="szIn szIn-9"><LiveOperationCard /></div>

          <div className="side-foot szIn szIn-10">
            <div className="side-foot-stat">
              <div className="side-foot-stat-item">
                <strong>+12K</strong>
                <span>conversas/dia</span>
              </div>
              <div className="side-foot-stat-item">
                <strong>1m25s</strong>
                <span>tempo médio</span>
              </div>
              <div className="side-foot-stat-item">
                <strong>99.9%</strong>
                <span>uptime</span>
              </div>
            </div>
            <span className="side-version">v2.0</span>
          </div>
        </div>
      </aside>

      {/* RIGHT — login form */}
      <div className="login-form-wrap">
        <div className="form-top">
          <span />
          <span className="form-top-link">
            Novo na Safe Zap?{" "}
            <a href="mailto:contato@safezap.com">Criar conta</a>
          </span>
        </div>

        <div className="form-body">
          <h1 className="form-title">Entrar na sua conta</h1>
          <p className="form-sub">Acesse o CRM, suas conversas e o pipeline da sua operação.</p>

          <form onSubmit={login}>
            <div className="field" style={{ marginBottom: 14 }}>
              <label className="field-label-v2" htmlFor="login-email">E-mail</label>
              <div className="field-input">
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="voce@empresa.com"
                  autoComplete="email"
                />
                <span className="field-input-icon"><Icon name="mail" size={14} /></span>
              </div>
            </div>

            <div className="field" style={{ marginBottom: 8 }}>
              <label className="field-label-v2" htmlFor="login-password" style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Senha</span>
                <a href="#esqueci" style={{ fontSize: 11.5, color: "var(--text-4)", fontWeight: 400 }}>Esqueci minha senha</a>
              </label>
              <div className="field-input">
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Sua senha de acesso"
                  autoComplete="current-password"
                />
                <span className="field-input-icon"><Icon name="lock" size={14} /></span>
                <button
                  type="button"
                  className="field-input-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  tabIndex={-1}
                >
                  <Icon name={showPassword ? "eye-off" : "eye"} size={14} />
                </button>
              </div>
            </div>

            <div className="options-row">
              <label className="login-check-label" onClick={() => setRemember(v => !v)}>
                <span className={`login-check-box${remember ? " login-check-box--on" : ""}`}>
                  {remember && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path d="M1 4l3 3 5-6" stroke="#001a0d" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </span>
                Lembrar este email neste dispositivo
              </label>
            </div>

            {error && (
              <div className="modal-callout modal-callout-warning" style={{ marginBottom: 16 }}>
                <Icon name="bell" size={14} />
                <span>{error}</span>
              </div>
            )}

            <button className="btn-submit" type="submit" disabled={loading}>
              {retrying ? "Tentando novamente…" : loading ? "Entrando…" : "Entrar"}
              {!loading && <Icon name="arrowRight" size={14} />}
            </button>
          </form>

          {/* ── OU CONTINUE COM ─────────────────────────────────── */}
          <div className="login-divider">
            <span />
            <span className="login-divider-text">OU CONTINUE COM</span>
            <span />
          </div>

          <div className="login-social-row">
            <a href={`${API}/auth/google`} className="login-social-btn">
              {/* Google "G" colorido */}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Google
            </a>
            <a href={`${API}/auth/microsoft`} className="login-social-btn">
              {/* Microsoft 4-square logo */}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <rect x="1"  y="1"  width="10" height="10" fill="#F25022"/>
                <rect x="13" y="1"  width="10" height="10" fill="#7FBA00"/>
                <rect x="1"  y="13" width="10" height="10" fill="#00A4EF"/>
                <rect x="13" y="13" width="10" height="10" fill="#FFB900"/>
              </svg>
              Microsoft
            </a>
          </div>

          {/* ── Painel master ─────────────────────────────────────── */}
          <div className="login-master-row">
            <span className="login-master-label">Acesso administrativo da plataforma</span>
            <button
              type="button"
              className="login-master-btn"
              onClick={() => { setEmail("master@safezap.com"); setPassword(""); }}
              title="Login do operador SaaS — gerencia todas as empresas"
            >
              Painel master <Icon name="chevronRight" size={12} />
            </button>
          </div>
        </div>

        {/* ── Footer ────────────────────────────────────────────────── */}
        <div className="form-footer">
          <p className="form-footer-terms">
            Ao continuar, você concorda com os{" "}
            <a href="#termos">Termos</a> e{" "}
            <a href="#privacidade">Política de privacidade</a>.
          </p>
          <p className="form-footer-copy">© 2025 SafeCenter Soluções · Brasil</p>
        </div>
      </div>
    </div>
  );
}

export default LoginScreen;
